import { memo } from 'react';
import { Box, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { useStepperContext } from '@components/stepper';
import { useGenerateProductName } from '@travelity/api/services';
import StepContent from '@/views/add-product/components/step-content';
import type { PackageFormValues } from '../../add-package.types';

export type ProductDetailsStepProps = {
    errors: string[];
};

const PackageNameDescription = ({ errors }: ProductDetailsStepProps) => {
    const {
        control,
        setValue,
        register,
        formState: { errors: formErrors },
    } = useFormContext<PackageFormValues>();
    const { field } = useController({
        control,
        name: 'productDetails.name',
    });
    const { showErrors } = useStepperContext();
    const { data } = useGenerateProductName();

    const generatedName = (data?.name ?? '') as string;

    return (
        <StepContent title="Product Details" errors={errors}>
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
                <TextField
                    label="Package name"
                    margin="dense"
                    type="text"
                    placeholder="Enter package name or use generated name"
                    {...register('productDetails.name', {
                        required: 'Package name is required',
                    })}
                    error={!!(showErrors && formErrors.productDetails?.name)}
                    helperText={
                        showErrors && formErrors.productDetails?.name?.message
                    }
                    value={field.value}
                    InputProps={{
                        endAdornment: !field.value && generatedName && (
                            <InputAdornment position="end">
                                <Tooltip
                                    title="Use generated name"
                                    placement="right"
                                >
                                    <Box
                                        onClick={() => {
                                            setValue(
                                                'productDetails.name',
                                                generatedName,
                                                {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                }
                                            );
                                            field.onBlur();
                                        }}
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: '12px',
                                            textDecorationLine: 'underline',
                                            color: '#949BAC',
                                        }}
                                    >
                                        {generatedName}
                                    </Box>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    label="Description"
                    placeholder="Describe the package"
                    {...register('productDetails.description', {
                        required: 'Package description is required',
                    })}
                    error={
                        !!(showErrors && formErrors.productDetails?.description)
                    }
                    helperText={
                        showErrors &&
                        formErrors.productDetails?.description?.message
                    }
                    multiline
                    minRows={4}
                />
                <Box
                    sx={{
                        fontSize: 13,
                        color: 'text.secondary',
                        maxWidth: 460,
                    }}
                >
                    Add a short name and a clear description to help travelers
                    understand this package at a glance.
                </Box>
            </Stack>
        </StepContent>
    );
};

export default memo(PackageNameDescription);
